/**
 * StandardsXBRLTab — Standards browser + XBRL concept explorer
 * Left column: reference documents grouped by framework
 * Right column: searchable XBRL taxonomy with expandable detail
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_V1, safeFetch, authFetch } from '../../constants';

const KE = `${API_V1}/knowledge-engine`;

// --- shared styles ----------------------------------------------------------
const th: React.CSSProperties = {
  textAlign: 'left' as const, padding: '8px 10px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-subtle)',
  textTransform: 'uppercase' as const, letterSpacing: 0.5, cursor: 'pointer',
};
const td = (i: number): React.CSSProperties => ({
  padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-subtle)',
  background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
});
const badge = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 10,
  fontWeight: 600, background: bg, color: fg,
});
const sectionTitle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)',
};
const filterBtn = (active: boolean): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: 'none',
  background: active ? 'var(--accent)' : 'var(--bg-elevated)',
  color: active ? '#fff' : 'var(--text-secondary)',
  transition: 'all 0.15s',
});

interface XBRLConcept {
  framework: string;
  element_name: string;
  label: string;
  statement: string;
  account_type: string;
  normal_balance: string;
  asc_paragraph?: string;
  ifrs_reference?: string;
  definition?: string;
  alt_labels?: string[];
  parent_concept?: string;
  period_type?: string;
}

export default function StandardsXBRLTab() {
  // Standards state
  const [docs, setDocs] = useState<any[]>([]);
  const [docFilter, setDocFilter] = useState('ALL');
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  // XBRL state
  const [xbrlQuery, setXbrlQuery] = useState('');
  const [xbrlFw, setXbrlFw] = useState<'US_GAAP' | 'IFRS'>('US_GAAP');
  const [xbrlResults, setXbrlResults] = useState<XBRLConcept[]>([]);
  const [xbrlCount, setXbrlCount] = useState(0);
  const [xbrlLoading, setXbrlLoading] = useState(false);
  const [expandedXbrl, setExpandedXbrl] = useState<string | null>(null);
  const [xbrlDetail, setXbrlDetail] = useState<any>(null);
  const [sortCol, setSortCol] = useState<string>('element_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Load standards
  useEffect(() => {
    safeFetch(`${API_V1}/library/documents`, { documents: [] }).then(r => setDocs(r?.documents || []));
  }, []);

  // Search XBRL
  const searchXbrl = useCallback(() => {
    setXbrlLoading(true);
    const q = encodeURIComponent(xbrlQuery);
    safeFetch(`${KE}/xbrl/search?q=${q}&framework=${xbrlFw}&limit=100`, { results: [], count: 0 })
      .then(r => {
        setXbrlResults(r?.results || []);
        setXbrlCount(Number(r?.total) || Number(r?.count) || 0);
        setXbrlLoading(false);
      });
  }, [xbrlQuery, xbrlFw]);

  useEffect(() => {
    const t = setTimeout(searchXbrl, 300);
    return () => clearTimeout(t);
  }, [searchXbrl]);

  // Load XBRL detail on expand
  useEffect(() => {
    if (!expandedXbrl) { setXbrlDetail(null); return; }
    const parts = expandedXbrl.split(':');
    if (parts.length < 2) return;
    authFetch(`${KE}/xbrl/${parts[0]}/${parts[1]}`).then(r => r.ok ? r.json() : null).then(setXbrlDetail).catch(() => setXbrlDetail(null));
  }, [expandedXbrl]);

  // Standards filter
  const frameworks = ['ALL', ...Array.from(new Set(docs.map(d => d.framework)))];
  const filteredDocs = docFilter === 'ALL' ? docs : docs.filter(d => d.framework === docFilter);
  const grouped: Record<string, any[]> = {};
  for (const d of filteredDocs) {
    const key = d.framework;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(d);
  }

  // Sort XBRL results
  const sorted = [...xbrlResults].sort((a: any, b: any) => {
    const av = (a[sortCol] || '').toString().toLowerCase();
    const bv = (b[sortCol] || '').toString().toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIndicator = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div style={{ display: 'flex', gap: 20 }}>
      {/* Left: Standards Browser */}
      <div style={{ width: '38%', flexShrink: 0 }}>
        <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Standards ({docs.length})</span>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
          {frameworks.map(f => (
            <button key={f} onClick={() => setDocFilter(f)} style={filterBtn(docFilter === f)}>{f}</button>
          ))}
        </div>
        <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
          {Object.entries(grouped).sort().map(([fw, list]) => (
            <div key={fw} style={{
              background: 'var(--bg-card)', borderRadius: 8,
              border: '1px solid var(--border-subtle)', padding: 14, marginBottom: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 8 }}>{fw}</div>
              {list.sort((a, b) => (a.document_code || '').localeCompare(b.document_code || '')).map(d => (
                <div
                  key={d.id || d.document_code}
                  onClick={() => setSelectedDoc(selectedDoc?.document_code === d.document_code ? null : d)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', padding: '5px 0',
                    borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                    background: selectedDoc?.document_code === d.document_code ? 'rgba(34,211,238,0.08)' : 'transparent',
                    borderRadius: 4, paddingLeft: 6, paddingRight: 6,
                  }}
                >
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', marginRight: 6 }}>
                      {d.document_code || d.standard_code}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{d.title || d.standard_name || ''}</span>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>{d.issuing_body}</span>
                </div>
              ))}
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 6 }}>{list.length} standards</div>
            </div>
          ))}
          {filteredDocs.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
              No documents found for this framework.
            </div>
          )}
        </div>
        {/* Selected doc detail */}
        {selectedDoc && (
          <div style={{
            marginTop: 12, padding: 14, background: 'var(--bg-surface)',
            borderRadius: 8, border: '1px solid var(--border-subtle)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              {selectedDoc.document_code || selectedDoc.standard_code}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
              {selectedDoc.title || selectedDoc.standard_name}
            </div>
            {selectedDoc.description && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>{selectedDoc.description}</div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              {selectedDoc.issuing_body} · {selectedDoc.framework}
              {selectedDoc.effective_date && ` · Effective: ${new Date(selectedDoc.effective_date).toLocaleDateString()}`}
            </div>
          </div>
        )}
      </div>

      {/* Right: XBRL Explorer */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>XBRL Concepts ({xbrlCount})</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setXbrlFw('US_GAAP')} style={filterBtn(xbrlFw === 'US_GAAP')}>US GAAP</button>
            <button onClick={() => setXbrlFw('IFRS')} style={filterBtn(xbrlFw === 'IFRS')}>IFRS</button>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Search XBRL concepts..."
            value={xbrlQuery}
            onChange={e => setXbrlQuery(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', fontSize: 12, borderRadius: 6,
              border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
              color: 'var(--text-primary)', outline: 'none',
            }}
          />
        </div>
        {xbrlLoading ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Searching...</p>
        ) : (
          <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
              <thead>
                <tr>
                  <th style={th} onClick={() => toggleSort('element_name')}>Element{sortIndicator('element_name')}</th>
                  <th style={th} onClick={() => toggleSort('label')}>Label{sortIndicator('label')}</th>
                  <th style={th} onClick={() => toggleSort('statement')}>Stmt{sortIndicator('statement')}</th>
                  <th style={th} onClick={() => toggleSort('account_type')}>Type{sortIndicator('account_type')}</th>
                  <th style={th} onClick={() => toggleSort('normal_balance')}>Bal{sortIndicator('normal_balance')}</th>
                  <th style={th}>Ref</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c, i) => {
                  const key = `${c.framework}:${c.element_name}`;
                  const isExpanded = expandedXbrl === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        onClick={() => setExpandedXbrl(isExpanded ? null : key)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td style={{ ...td(i), fontVariantNumeric: 'tabular-nums', fontSize: 11, fontWeight: 600, color: '#60A5FA' }}>
                          {c.element_name}
                        </td>
                        <td style={td(i)}>{c.label}</td>
                        <td style={td(i)}>
                          <span style={badge(
                            c.statement === 'PL' ? '#FB923C20' : '#60A5FA20',
                            c.statement === 'PL' ? '#FB923C' : '#60A5FA'
                          )}>{c.statement}</span>
                        </td>
                        <td style={{ ...td(i), fontSize: 11 }}>{c.account_type}</td>
                        <td style={{ ...td(i), fontSize: 11 }}>{c.normal_balance}</td>
                        <td style={{ ...td(i), fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
                          {c.asc_paragraph || c.ifrs_reference || '--'}
                        </td>
                      </tr>
                      {isExpanded && xbrlDetail && (
                        <tr>
                          <td colSpan={6} style={{ padding: '12px 16px', background: 'rgba(96,165,250,0.06)', borderBottom: '1px solid var(--border-subtle)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                              <div>
                                <div style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, marginBottom: 2 }}>DEFINITION</div>
                                <div style={{ color: 'var(--text-secondary)' }}>{xbrlDetail.definition || 'N/A'}</div>
                              </div>
                              <div>
                                <div style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, marginBottom: 2 }}>DETAILS</div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                                  Period: {xbrlDetail.period_type || 'N/A'}<br />
                                  Parent: {xbrlDetail.parent_concept || 'None'}<br />
                                  {xbrlDetail.asc_topic && <>ASC Topic: {xbrlDetail.asc_topic}-{xbrlDetail.asc_subtopic}<br /></>}
                                  {xbrlDetail.ifrs_reference && <>IFRS: {xbrlDetail.ifrs_reference}<br /></>}
                                </div>
                              </div>
                            </div>
                            {xbrlDetail.alt_labels && xbrlDetail.alt_labels.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <span style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600 }}>ALT LABELS: </span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{xbrlDetail.alt_labels.join(', ')}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {xbrlResults.length === 0 && !xbrlLoading && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
                {xbrlQuery ? 'No concepts found for this search.' : 'Enter a search term or browse all concepts.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
